/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { TViewComponent } from "../../../view/IView";
import { IntegrationEditorCreate } from "./IntegrationEditorCreate";
import {
	IIntegrationEditorCreateListProps,
	IntegrationEditorCreateList
} from "./components/IntegrationEditorCreateList";
import { Layout } from "../../../ui/components/Layout/Layout";
import { IntegrationEditorCreateDetails } from "./components/IntegrationEditorCreateDetails";
import { IEditorApiIntegrationResponse } from "@adapptio/lib-core";
import { useSimpleEvent } from "../../../ui/hooks/useSimpleEvent";

export const IntegrationEditorCreateComponent: TViewComponent<IntegrationEditorCreate> = ({ view }) => {
	const layoutState = useMemo(() => view.getLayout(), []);
	const [ creating, setCreating ] = useState(view.isSaving());

	const [ selectedIntegration, setSelectedIntegration ] = useState<IEditorApiIntegrationResponse>(null);

	const createIntegration = useCallback(
		(integration: IEditorApiIntegrationResponse, name: string, basePath: string) => {
			view.createIntegration(integration, name, basePath);
		},
		[]
	);

	const selectIntegration = useCallback<IIntegrationEditorCreateListProps["onIntegrationSelect"]>(
		(integration) => {
			setSelectedIntegration(integration);
		},
		[ setSelectedIntegration ]
	);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "integrations":
				return (
					<IntegrationEditorCreateList
						view={view}
						selectedIntegration={selectedIntegration}
						onIntegrationSelect={selectIntegration}
					/>
				);
			case "details":
				return (
					<IntegrationEditorCreateDetails
						selectedIntegration={selectedIntegration}
						onSubmitConfig={createIntegration}
						creating={creating}
						view={view}
					/>
				);
			default:
				return <p>Error: Undefined panel {componentName}.</p>;
		}
	};

	useSimpleEvent(view.onSaveStateChange, () => {
		setCreating(view.isSaving());
	});

	return <Layout state={layoutState} renderComponent={renderComponent} />;
};
